export type AnnouncementStatus = "proposed" | "published" | "archived";

export interface Announcement {
  id: string;
  title: string;
  body: string;
  status: AnnouncementStatus;
  allowed_roles: string[];
  is_deployment: boolean;
  /** Marked important at publication — pinned to the top and pulsing until read. */
  is_important: boolean;
  is_read: boolean;
  published_at: string | null;
  created_by_name: string | null;
  published_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementRecipient {
  id: string;
  name: string;
  email: string;
  role: string;
  role_label: string;
  job_title: string;
  /** "role" — targeted by allowed_roles; "manager" — standing access (dev/CEO/director). */
  access_reason: "role" | "manager";
  is_read: boolean;
  read_at: string | null;
}

export interface AnnouncementAudience {
  read: AnnouncementRecipient[];
  unread: AnnouncementRecipient[];
  total: number;
  read_count: number;
  unread_count: number;
}

/** State of the blocking "changes since last login" modal (Wrocław coordinators/directors). */
export interface LoginChanges {
  show: boolean;
  announcements: Announcement[];
}

export interface AnnouncementFormData {
  title: string;
  body: string;
  allowed_roles: string[];
  is_important: boolean;
}

/** Important announcements first, then the backend's own ordering (newest first). */
export function sortAnnouncements<T extends { is_important: boolean }>(items: T[]): T[] {
  return [...items].sort((a, b) => Number(b.is_important) - Number(a.is_important));
}

// Internal recipient roles — the default audience of a new announcement.
export const INTERNAL_ANNOUNCEMENT_ROLES: { value: string; label: string }[] = [
  { value: "director", label: "Dyrektor" },
  { value: "coordinator", label: "Koordynator" },
  { value: "employee", label: "Pracownik" },
  { value: "accountant", label: "Księgowy" },
  { value: "hr", label: "HR" },
];

/** Subcontractors (external) see only published announcements targeting this role. */
export const CONTRACTOR_ANNOUNCEMENT_ROLE = "contractor";

// Recipient roles a manager can target (mirrors ANNOUNCEMENT_ROLE_CHOICES).
export const ANNOUNCEMENT_ROLES: { value: string; label: string }[] = [
  ...INTERNAL_ANNOUNCEMENT_ROLES,
  { value: CONTRACTOR_ANNOUNCEMENT_ROLE, label: "Podwykonawca" },
];
